import React, { createContext, useState, useEffect, useRef, useMemo, forwardRef } from 'react';
import FontManager from './FontManager'
import { fabric } from 'fabric'
import debounce from 'debounce'
import i18n from '@/i18n'
import * as ShortsHelper from '@/utils/ShortsHelper'
import ShortsApis from '@/utils/Videomonster/Apis/Shorts'
import DataManager from '@/utils/DataManager'

export const YoutubeUrlToShortsContext = createContext();
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const LEFT_TAB_ITEMS = [
    {
        key: 'script',
        label: i18n.t("global.script"),
    },
    {
        key: 'layout',
        label: i18n.t("global.layout"),
    },
    {
        key: 'font',
        label: i18n.t("global.text"),
    },
    // {
    //     key: 'music',
    //     label: '음악',
    // },
    // {
    //     key: 'ai-dubbing',
    //     label: 'AI더빙',
    // }
]

const DEFAULT_FONT_FAMILY = {
    TOPIC: 'NEXONFootballGothicB',
    SENTENCE: 'NEXONLv2GothicMedium',
}

const SCALE_WIDTH = 80;
const ROW_HEIGHT = 80;
const AUDIO_ROW_HEIGHT = 32;

const FRAME_RATE = 60
const FRAME_TIME = 1000 / FRAME_RATE

export const withYoutubeUrlToShortsProvider = (WrappedComponent) => {
    return forwardRef((props, ref) => {
        const {
            index,
            shortsId,
            isPreviewMode,
            isEditor,
            initData: {
                metadataJson,
                videoUrl,
                audioUrl,
                previewImageUrl,
                initData
            },
            onBeforePlayVideo,
        } = props
        const [lastUpdated, setLastUpdated] = useState(null);

        const token = DataManager.GetToken();
        const [isInitialized, setIsInitialized] = useState(false)
        const [isUploading, setIsUploading] = useState(false)

        const [width, setWidth] = useState(270)
        const [height, setHeight] = useState(480)
        const currentCanvasScale = height / 480

        const [now, setNow] = useState(Date.now())
        const forceUpdate = () => setNow(now + 1)

        const [leftTab, setLeftTab] = useState(LEFT_TAB_ITEMS[0].key)
        const [timelineScale, setTimelineScale] = useState(1)

        const currentScaleWidth = useMemo(() => {
            return SCALE_WIDTH * timelineScale
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [timelineScale])

        const materialRef = useRef(null)
        const timelineData = {
            topic: materialRef.current ? materialRef.current.topic : {
                text: ''
            },
            sentences: []
        }
        if (materialRef.current && materialRef.current.textMap) {
            for (const id in materialRef.current.textMap) {
                timelineData.sentences.push(materialRef.current.textMap[id])
            }
        }
        timelineData.sentences.sort((a, b) => a.start - b.start)

        const isCanRendering = timelineData.sentences.filter(sentence => sentence.enabled).length > 0

        const [layoutType, setLayoutType] = useState((initData && initData.layout) || 'Layout01')
        const [fonts, setFonts] = useState([]);

        const [currentTimelineActions, setCurrentTimelineActions] = useState([])
        const [currentSentence, setCurrentSentence] = useState(null)
        const [currentActiveObject, setCurrentActiveObject] = useState(null)

        const canvasIdRef = useRef(null)
        const fabricCanvasRef = useRef(null)
        const videoWrapperRef = useRef(null)

        const isSavingRef = useRef(false)
        const isRenderingRef = useRef(false)

        const isPlayingRef = useRef(false)
        const videoRef = useRef(null)

        const [isPlaying, setIsPlaying] = useState(false)
        const [isPaused, setIsPaused] = useState(false)

        const [videoVolume, setVideoVolume] = useState(1)
        const [videoPlaybackSpeed, setVideoPlaybackSpeed] = useState(1)
        const [videoTime, setVideoTime] = useState(0)
        const [videoRect, setVideoRect] = useState({ x: 0, y: 0, width: Number(width), height: Number(width) })

        const timelineRef = useRef(null)

        // function handleResizeWindow() {
        //     const windowHeight = window.innerHeight - 293
        //     const windowWidth = windowHeight / (16 / 9)

        //     setWidth(windowWidth)
        //     setHeight(windowHeight)

        //     updateCanvasScale()
        // }

        // useEffect(() => {
        //     if (isEditor) {
        //         handleResizeWindow()
        //         window.addEventListener('resize', handleResizeWindow)
        //         return () => {
        //             window.removeEventListener('resize', handleResizeWindow)
        //         }
        //     }
        // }, [])

        useEffect(() => {
            async function initialize() {
                await FontManager.loadFonts()
                const filteredFonts = FontManager.getFonts().filter(font => {
                    return font && font.lang && font.lang.ko === true && font.postscriptName
                })
                setFonts(filteredFonts);

                if (canvasIdRef.current && timelineData.sentences.length === 0) {
                    let layout
                    let topic
                    let sentences
                    let allSentences
                    let volume
                    let playbackSpeed
    
                    if (!shortsId) {
                        layout = initData.layout
                        topic = {
                            text: initData.topic
                        }
                        sentences = initData.sentences
                        allSentences = initData.allSentences
                        volume = 1
                        playbackSpeed = 1
                    }
                    else {
                        const { data: { item } } = await ShortsApis.GetShortsItem(token, { shortsId })
                        let {
                            layout: layoutFromServer,
                            material
                        } = item
    
                        if (!material) {
                            layout = initData.layout
                            topic = {
                                text: initData.topic
                            }
                            sentences = initData.sentences
                            allSentences = initData.allSentences
                            volume = 1
                            playbackSpeed = 1
                        }
                        else {
                            material = JSON.parse(material)

                            layout = layoutFromServer
                            topic = material.topic
                            sentences = material.sentences
                            allSentences = material.allSentences
                            volume = material.volume
                            playbackSpeed = material.playbackSpeed
                        }
                    }
    
                    materialRef.current = ShortsHelper.ParseShortsAnalysisToMaterial({
                        layout,
                        topic,
                        sentences,
                        allSentences,
                        volume,
                        playbackSpeed
                    })

                    materialRef.current.textMap = {}
                    materialRef.current.textMap[materialRef.current.topic.id] = materialRef.current.topic
                    materialRef.current.allSentences.forEach(sentence => {
                        materialRef.current.textMap[sentence.id] = sentence
                    })
                    sortSentences()
    
                    const needToLoadFontMap = {}
                    for (const id in materialRef.current.textMap) {
                        const { textOptions } = materialRef.current.textMap[id]
                        if (textOptions && textOptions.fontFamily) {
                            needToLoadFontMap[textOptions.fontFamily] = true
                        }
                    }
                    
                    await Promise.all([
                        ...Object.keys(needToLoadFontMap),
                        DEFAULT_FONT_FAMILY.TOPIC,
                        DEFAULT_FONT_FAMILY.SENTENCE,
                    ].map(async fontFamily => {
                        await FontManager.loadFontFace(fontFamily)
                        await sleep(25)
                    }))
    
                    await initCanvas()

                    while (!videoRef.current) {
                        await sleep(100)
                    }
                    updateVideoVolume(volume)
                    updatePlaybackSpeed(playbackSpeed)
                    updateLayout(layout)
                    handleStopVideo()

                    setIsInitialized(true)
                }
            }
            initialize()
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [canvasIdRef.current])

        const getVideoStartTime = () => {
            if (!materialRef.current || !materialRef.current.textMap) return 0

            const sentenceIds = Object.keys(materialRef.current.textMap)
            for (const id of sentenceIds) {
                const sentence = materialRef.current.textMap[id]
                if (sentence.enabled) {
                    return sentence.start + 0.01
                }
            }
            return 0
        }

        const getVideoEndTime = () => {
            if (!materialRef.current || !materialRef.current.textMap) return 0

            const sentenceIds = Object.keys(materialRef.current.textMap)
            sentenceIds.sort((a, b) => materialRef.current.textMap[b].end - materialRef.current.textMap[a].end)

            for (const id of sentenceIds) {
                const sentence = materialRef.current.textMap[id]
                if (sentence.enabled) {
                    return sentence.end - 0.01
                }
            }
            return 0
        }

        const setCurrentVideoTime = time => {
            videoRef.current.currentTime = time
            setVideoTime(time)
        }

        const handleMoveToStart = () => {
            if (!videoRef.current) return;

            isPlayingRef.current = false
            setIsPlaying(false)
            setIsPaused(true)
            videoRef.current.pause()

            updateVideoCurrentTime(getVideoStartTime())
            resetFocusInCanvasObjects().catch(() => {})
        }

        const handleMoveToEnd = () => {
            if (!videoRef.current) return;

            isPlayingRef.current = false
            setIsPlaying(false)
            setIsPaused(true)
            videoRef.current.pause()

            updateVideoCurrentTime(getVideoEndTime())
            resetFocusInCanvasObjects().catch(() => {})
        }

        const handlePlayVideo = () => {
            try {
                if (!videoRef.current) return
                if (isPlayingRef.current) return

                if (onBeforePlayVideo) {
                    onBeforePlayVideo({ index, shortsId })
                };
    
                isPlayingRef.current = true
                setIsPlaying(true)
                setIsPaused(false)
    
                updateCurrentSentence(null)
    
                videoRef.current.play()
    
                playVideoCallback()
            }
            catch (e) {
            }
        }

        const handleUpdateLayout = layoutType => {
            updateLayout(layoutType, { reAlignText: true })
        }

        const handlePauseVideo = () => {
            try {
                if (videoRef.current.paused) {
                    videoRef.current.play()
                    setIsPaused(false)
                }
                else {
                    updateCurrentSentence(null)
                    videoRef.current.pause()
                    setIsPaused(true)
                }
            }
            catch (e) {
            }
        }

        const handleStopVideo = () => {
            try {
                isPlayingRef.current = false
                setIsPlaying(false)
                setIsPaused(true)
    
                updateCurrentSentence(null)
    
                const startTime = getVideoStartTime()
                setCurrentVideoTime(startTime)
                updateCanvasByCurrentTime(startTime)
    
                videoRef.current.pause()
    
                if (timelineRef.current) {
                    timelineRef.current.setTime(startTime);
                    timelineRef.current.setScrollLeft(startTime * currentScaleWidth);
                }

                resetFocusInCanvasObjects().catch(() => {})
            }
            catch (e) {
            }

            forceUpdate()
        }

        const updateCanvasByCurrentTime = currentTime => {
            try {
                const texts = fabricCanvasRef.current.getObjects().filter(obj => obj.className === 'sentence-text')

                if (texts.length > 0) {
                    let targetIndex = -1
                    let isTargetSentenceEnabled = false

                    for (let i = texts.length - 1; i >= 0; i--) {
                        const text = texts[i]
                        const sentence = materialRef.current.textMap[text.textId]
                        if (!sentence) continue

                        if (currentTime >= sentence.start && currentTime <= sentence.end) {
                            targetIndex = i
                            isTargetSentenceEnabled = sentence.enabled
                            break
                        }
                    }

                    for (let i = 0; i < texts.length; i++) {
                        texts[i].visible = i === targetIndex && isTargetSentenceEnabled
                        texts[i].selectable = i === targetIndex && isTargetSentenceEnabled
                    }
                }
                fabricCanvasRef.current.discardActiveObject()
                fabricCanvasRef.current.renderAll()

                if (timelineRef.current) {
                    timelineRef.current.setTime(currentTime)
                }
            }
            catch (e) {
                console.error(e)
            }
        }


        function waitForSeek() {
            return new Promise(resolve => {
                const check = () => {
                    if (!videoRef.current || videoRef.current.readyState >= 2) {
                        resolve()
                    }
                    else {
                        setTimeout(check, FRAME_TIME)
                    }
                }
                check()
            })
        }

        const playVideoCallback = async () => {
            try {
                if (!videoRef.current) return
    
                const enabledSentences = timelineData.sentences.filter(sentence => sentence.enabled)
    
                for (let i = 0; i < enabledSentences.length; i++) {
                    if (!isPlayingRef.current) break
    
                    let start = enabledSentences[i].start
                    let end = enabledSentences[i].end

                    if (end <= videoRef.current.currentTime) continue
    
                    // 다음 캡션들과 1초 이하로 이어지면 묶어서 재생
                    while (
                        i + 1 < enabledSentences.length &&
                        enabledSentences[i + 1].start - end <= 1
                    ) {
                        end = enabledSentences[i + 1].end
                        i++
                    }
    
                    videoRef.current.currentTime = start
                    await waitForSeek()
                    videoRef.current.play()
    
                    while (videoRef.current.currentTime < end) {
                        const currentTime = videoRef.current.currentTime
                        setVideoTime(currentTime)
                        updateCanvasByCurrentTime(currentTime)

                        if (timelineRef.current) {
                            timelineRef.current.setScrollLeft(currentTime * currentScaleWidth)
                        }

                        if (!isPlayingRef.current) break
                        await sleep(FRAME_TIME)
                    }
    
                    videoRef.current.pause()
                }
    
                handleStopVideo()
            }
            catch (e) {
            }
        }

        const updateVideoVolume = value => {
            if (videoRef.current) {
                videoRef.current.volume = value
            }
            if (materialRef.current) {
                materialRef.current.volume = value
            }

            setVideoVolume(value)
            setLastUpdated(new Date())
        }

        const updatePlaybackSpeed = speed => {
            if (videoRef.current) {
                videoRef.current.playbackRate = speed
            }
            if (materialRef.current) {
                materialRef.current.playbackSpeed = speed
            }

            setVideoPlaybackSpeed(speed)
            setLastUpdated(new Date())
        }

        const updateVideoCurrentTime = debounce((time, { updateTimeline = true } = {}) => {
            let isTimeOutOfRange = false
            const startTime = getVideoStartTime()
            const endTime = getVideoEndTime()

            if (time < startTime) {
                time = startTime
                isTimeOutOfRange = true
            }
            else if (time > endTime) {
                time = endTime
                isTimeOutOfRange = true
            }
            const isNeedToUpdateTimeline = isTimeOutOfRange || updateTimeline
            
            if (videoRef.current) {
                setCurrentVideoTime(time)
            }
            updateCanvasByCurrentTime(time)

            if (timelineRef.current) {
                timelineRef.current.setTime(time)
                if (isNeedToUpdateTimeline) {
                    timelineRef.current.setScrollLeft(time * currentScaleWidth)
                }
            }
        }, FRAME_TIME * 2)

        const sortSentences = () => {
            materialRef.current.sentences.sort((a, b) => a.start - b.start)

            for (let i = 0; i < materialRef.current.sentences.length - 1; i++) {
                const current = materialRef.current.sentences[i]
                const next = materialRef.current.sentences[i + 1]
                
                if (current.end > next.start) {
                    current.end = next.start
                }
            }
        }

        const updateSentence = sentence => {
            for (let i = 0; i < materialRef.current.sentences.length; i++) {
                if (materialRef.current.sentences[i].id === sentence.id) {
                    materialRef.current.sentences[i] = sentence
                    break;
                }
            }
            materialRef.current.textMap[sentence.id] = sentence
            sortSentences()

            forceUpdate()
            setLastUpdated(new Date())
        }

        const updateSentences = sentences => {
            materialRef.current.sentences = sentences
            sentences.forEach(sentence => {
                materialRef.current.textMap[sentence.id] = sentence
            })
            sortSentences()

            forceUpdate()
            setLastUpdated(new Date())
        }

        const updateCurrentSentence = sentence => {
            setCurrentSentence(sentence)
            if (!fabricCanvasRef.current) return;

            let targetSentenceText = fabricCanvasRef.current.getObjects().forEach(obj => {
                if (obj.className === 'sentence-text') {
                    if (sentence && obj.textId === sentence.id) {
                        targetSentenceText = obj
                        obj.selectable = true
                        obj.visible = true
                    }
                    else {
                        obj.selectable = false
                        obj.visible = false
                    }
                }
            })

            if (targetSentenceText) {
                setCurrentActiveObject(targetSentenceText)
                fabricCanvasRef.current.setActiveObject(targetSentenceText)
            }
            else {
                setCurrentActiveObject(null)
                fabricCanvasRef.current.discardActiveObject()
            }
            fabricCanvasRef.current.renderAll()
        }

        const updateTextOptions = async (updates) => {
            if (!currentActiveObject) return;

            const options = {}

            // 서식 업데이트
            if (updates.fontSize !== undefined) options.fontSize = updates.fontSize;
            if (updates.fill !== undefined) options.fill = updates.fill;
            if (updates.textAlign !== undefined) options.textAlign = updates.textAlign;
            if (updates.linethrough !== undefined) options.linethrough = updates.linethrough;

            if (updates.fontWeight !== undefined) options.fontWeight = updates.fontWeight;
            if (updates.fontStyle !== undefined) options.fontStyle = updates.fontStyle;

            if (updates.underline !== undefined) options.underline = updates.underline;
            if (updates.lineHeight !== undefined) options.lineHeight = updates.lineHeight;

            // 외곽선 업데이트
            if (updates.strokeWidth !== undefined) options.strokeWidth = updates.strokeWidth;
            if (updates.stroke !== undefined) options.stroke = updates.stroke;

            // 그림자 업데이트
            // if (updates.shadow !== undefined) {
            // if (updates.shadow === null) {
            //     currentActiveObject.shadow = null
            // }
            // else {
            //     if (!currentActiveObject.shadow) {
            //         currentActiveObject.shadow = new fabric.Shadow({
            //             color: 'white',
            //             blur: 5,
            //             offsetX: Math.cos(Math.PI / 6) * 10,
            //             offsetY: Math.sin(Math.PI / 6) * 10,
            //         })
            //     }
            //     if (updates.shadow.color) currentActiveObject.shadow.color = updates.shadow.color
            //     if (updates.shadow.blur) currentActiveObject.shadow.blur = updates.shadow.blur
            //     if (updates.shadow.distance) currentActiveObject.shadow.distance = updates.shadow.distance
            //     if (updates.shadow.angle) currentActiveObject.shadow.angle = updates.shadow.angle
            // }
            // }

            // 폰트 업데이트
            if (updates.fontFamily !== undefined) {
                await FontManager.loadFontFace(updates.fontFamily)
                await sleep(25)

                options.fontFamily = updates.fontFamily;
            }

            if (updates.layout !== undefined) {
                currentActiveObject.angle = 0
                if (updates.layout === 'left') {
                    options.left = 50
                }
                else if (updates.layout === 'center') {
                    options.left = (fabricCanvasRef.current.width - (currentActiveObject.width * currentActiveObject.scaleX)) / 2
                }
                else if (updates.layout === 'right') {
                    options.left = fabricCanvasRef.current.width - (currentActiveObject.width * currentActiveObject.scaleX) - 50
                }
            }
            // // 캔버스에 변경사항 적용
            currentActiveObject.set(options);
            fabricCanvasRef.current.renderAll();

            fabricCanvasRef.current.setActiveObject(currentActiveObject)
            setCurrentActiveObject(currentActiveObject)
            
            forceUpdate()
            setLastUpdated(new Date())
        };

        function setCanvasId(canvasId) {
            canvasIdRef.current = canvasId
        }

        async function initCanvas() {
            const sentences = []
            for (const key in materialRef.current.textMap) {
                sentences.push(materialRef.current.textMap[key])
            }

            const fabricCanvas = new fabric.Canvas(canvasIdRef.current, {
                width,
                height,
                interactive: isEditor
            })

            fabricCanvas.on('selection:created', e => {
                setCurrentActiveObject(e.target)
            })

            fabricCanvas.on('selection:updated', e => {
                setCurrentActiveObject(e.target)
            })


            fabricCanvas.on('selection:cleared', e => {
                setCurrentActiveObject(null)
            })

            fabricCanvas.on('text:changed', (e) => {
                if (e.target.textId && materialRef.current.textMap[e.target.textId]) {
                    const sentence = materialRef.current.textMap[e.target.textId]
                    sentence.text = e.target.text
                    updateSentence(sentence)
                }
            })

            if (materialRef.current.topic && materialRef.current.topic.text) {
                const {
                    left = 0,
                    top = 0,
                    scaleX = 1,
                    scaleY = 1,
                    fontSize = 24,
                    fill = '#FFFFFF',
                    textAlign = 'center',
                    linethrough = false,
                    fontWeight = 'bold',
                    fontStyle = 'normal',
                    underline = false,
                    lineHeight = 1.0,
                    strokeWidth = 0,
                    stroke = '#000000',
                    fontFamily = DEFAULT_FONT_FAMILY.TOPIC,
                } = materialRef.current.topic.textOptions || {}

                const topicText = new fabric.IText(materialRef.current.topic.text, {
                    left,
                    top,
                    scaleX,
                    scaleY,
                    fontSize,
                    fill,
                    textAlign,
                    linethrough,
                    fontWeight,
                    fontStyle,
                    underline,
                    lineHeight,
                    strokeWidth,
                    stroke,
                    fontFamily,

                    padding: 10,
                    originX: 'center',
                    originY: 'center',
                    centeredScaling: true,
                    borderColor: '#FFFFFF',
                    transparentCorners: false,
                    cornerStyle: 'circle',
                    cornerColor: '#FFFFFF',
                    cornerStroke: 2,
                    cornerStrokeColor: '#000000',
                    cornerSize: 15,
                })

                topicText.textId = materialRef.current.topic.id
                topicText.className = 'topic-text'
                topicText.isNeedToAlign = !materialRef.current.topic.textOptions
                fabricCanvas.add(topicText)

                topicText.setControlsVisibility({
                    bl: true,
                    br: true,
                    mb: false,
                    ml: false,
                    mr: false,
                    mt: false,
                    tl: true,
                    tr: true,
                    mtr: false, // rotate
                })
            }

            if (Array.isArray(sentences)) {
                let isFirst = true

                sentences.forEach((sentence, sentenceIndex) => {
                    const {
                        left = 0,
                        top = 0,
                        scaleX = 1,
                        scaleY = 1,
                        fontSize = 18,
                        fill = '#FFFFFF',
                        textAlign = 'center',
                        linethrough = false,
                        fontWeight = 'normal',
                        fontStyle = 'normal',
                        underline = false,
                        lineHeight = 1.0,
                        strokeWidth = 0,
                        stroke = '#000000',
                        fontFamily = DEFAULT_FONT_FAMILY.SENTENCE,
                    } = sentence.textOptions || {}

                    const text = new fabric.IText(sentence.text, {
                        left,
                        top,
                        scaleX,
                        scaleY,
                        fontSize,
                        fill,
                        textAlign,
                        linethrough,
                        fontWeight,
                        fontStyle,
                        underline,
                        lineHeight,
                        strokeWidth,
                        stroke,
                        fontFamily,

                        selectable: false,
                        visible: false,
                        padding: 10,
                        originX: 'center',
                        originY: 'center',
                        centeredScaling: true,
                        borderColor: '#FFFFFF',
                        transparentCorners: false,
                        cornerStyle: 'circle',
                        cornerColor: '#FFFFFF',
                        cornerStroke: 2,
                        cornerStrokeColor: '#000000',
                        cornerSize: 15,
                    })

                    if (sentence.enabled && isFirst) {
                        isFirst = false
                        text.selectable = true
                        text.visible = true
                    }
                    text.isNeedToAlign = !sentence.textOptions

                    text.setControlsVisibility({
                        bl: true,
                        br: true,
                        mb: false,
                        ml: false,
                        mr: false,
                        mt: false,
                        tl: true,
                        tr: true,
                        mtr: false, // rotate
                    })

                    text.className = 'sentence-text'
                    text.textId = sentence.id

                    fabricCanvas.add(text)
                })
            }

            fabricCanvasRef.current = fabricCanvas
        }

        function resetFocusInCanvasObjects() {
            return new Promise((resolve, reject) => {
                // for FOCUS BUG
                const fabricCanvas = fabricCanvasRef.current
                if (isEditor && fabricCanvas instanceof fabric.Canvas) {
                    const sel = new fabric.ActiveSelection(fabricCanvas.getObjects().filter(obj => ['topic-text', 'sentence-text'].includes(obj.className)), {
                        canvas: fabricCanvas,
                    });
                    fabricCanvas.setActiveObject(sel)
    
                    setTimeout(() => {
                        try {
                            fabricCanvas.discardActiveObject()
                            fabricCanvas.renderAll()
                        }
                        catch (e) { }
                        resolve()
                    }, 1)
                }
            })
        }

        // function updateCanvasScale() {
            // if (fabricCanvasRef.current) {
            //     fabricCanvasRef.current.setWidth(width)
            //     fabricCanvasRef.current.setHeight(height)

            //     const objects = fabricCanvasRef.current.getObjects()
            //     objects.forEach(obj => {
            //         // obj.scaleX = obj.originalScaleX * currentCanvasScale
            //         // obj.scaleY = obj.originalScaleY * currentCanvasScale
            //     })
            //     fabricCanvasRef.current.renderAll()
            //     if (isInitialized) setLastUpdated(new Date())
            // }
        // }

        function updateLayout(layoutType, { reAlignText = false } = {}) {
            materialRef.current.layout = layoutType
            setLayoutType(layoutType)

            const REF_VALUE = width

            let topicTop = 0
            let contentTop = 0
            let videoRect = {
                x: 0,
                y: (fabricCanvasRef.current.height - REF_VALUE) / 2,
                width: REF_VALUE,
                height: REF_VALUE
            }
            switch (layoutType) {
                case 'Layout01':
                    topicTop = fabricCanvasRef.current.height * 0.2
                    contentTop = fabricCanvasRef.current.height * 0.8
                    videoRect = {
                        x: 0,
                        y: (fabricCanvasRef.current.height - (REF_VALUE / (16 / 9))) / 2,
                        width: REF_VALUE,
                        height: REF_VALUE / (16 / 9)
                    }
                    break

                case 'Layout02':
                    topicTop = fabricCanvasRef.current.height * 0.15
                    contentTop = fabricCanvasRef.current.height * 0.85
                    videoRect = {
                        x: 0,
                        y: 0,
                        width: REF_VALUE,
                        height: REF_VALUE / (9 / 16)
                    }
                    break

                case 'Layout03':
                    topicTop = fabricCanvasRef.current.height * 0.75
                    contentTop = fabricCanvasRef.current.height * 0.9
                    videoRect = {
                        x: 0,
                        y: fabricCanvasRef.current.height * 0.075,
                        width: REF_VALUE,
                        height: REF_VALUE
                    }
                    break

                case 'Layout04':
                    topicTop = fabricCanvasRef.current.height * 0.125
                    contentTop = fabricCanvasRef.current.height * 0.875
                    videoRect = {
                        x: 0,
                        y: (fabricCanvasRef.current.height - REF_VALUE) / 2,
                        width: REF_VALUE,
                        height: REF_VALUE
                    }
                    break

                case 'Layout05':
                    topicTop = fabricCanvasRef.current.height * 0.125
                    contentTop = fabricCanvasRef.current.height * 0.275
                    videoRect = {
                        x: 0,
                        y: fabricCanvasRef.current.height - REF_VALUE - fabricCanvasRef.current.height * 0.075,
                        width: REF_VALUE,
                        height: REF_VALUE
                    }
                    break

                case 'Layout06':
                    topicTop = fabricCanvasRef.current.height * 0.8
                    contentTop = fabricCanvasRef.current.height * 0.6
                    videoRect = {
                        x: 0,
                        y: fabricCanvasRef.current.height * 0.15,
                        width: REF_VALUE,
                        height: REF_VALUE
                    }
                    break

                default:
                    break
            }

            setVideoRect({ ...videoRect })
            if (isInitialized) setLastUpdated(new Date())

            const fabricCanvas = fabricCanvasRef.current
            if (fabricCanvas instanceof fabric.Canvas) {
                const objects = fabricCanvas.getObjects()
                objects.forEach(obj => {
                    if (obj instanceof fabric.IText) {
                        let isNeedToUpdate = false
                        if (reAlignText) {
                            isNeedToUpdate = true
                        }
                        else if (obj.isNeedToAlign) {
                            isNeedToUpdate = true
                            delete obj.isNeedToAlign
                        }
    
                        if (isNeedToUpdate) {
                            if (obj.className === 'topic-text') {
                                obj.top = topicTop
                            }
                            else if (obj.className === 'sentence-text') {
                                obj.top = contentTop
                            }
                            obj.left = fabricCanvas.width / 2
                        }
                    }
                })
                fabricCanvas.renderAll()
                resetFocusInCanvasObjects().catch(() => {})
            }
        }

        async function extractReneringDatas() {
            const results = []
            const textMap = {}
            const VIDEO_SCALE = 1080 / width

            const texts = fabricCanvasRef.current.getObjects().filter(obj => {
                if (obj.className === 'topic-text') return true

                const sentence = materialRef.current.textMap[obj.textId]
                return (obj.className === 'sentence-text' && sentence.enabled)
            })

            const cloneText = text => {
                return new Promise(resolve => {
                    text.clone(resolve)
                })
            }

            for (const text of texts) {
                const { textId, className } = text

                const originLeft = text.left
                const originTop = text.top

                const clonedText = await cloneText(text)
                clonedText.set({
                    left: 9999,
                    top: 9999,
                    visible: true,
                })

                let start
                let end

                if (className !== 'topic-text') {
                    start = materialRef.current.textMap[textId].start
                    end = materialRef.current.textMap[textId].end
                }

                const width = text.width * text.scaleX * VIDEO_SCALE
                const height = text.height * text.scaleY * VIDEO_SCALE
                const left = (originLeft * VIDEO_SCALE - (width / 2))
                const top = (originTop * VIDEO_SCALE - (height / 2))
                
                results.push({
                    id: textId,
                    filename: `${textId}.png`,
                    type: className,

                    start,
                    end,

                    left,
                    top,
                    width,
                    height,

                    dataURL: null
                })

                textMap[textId] = clonedText
            }
            fabricCanvasRef.current.renderAll()

            results.forEach(text => {
                const textObj = textMap[text.id]
                if (textObj instanceof fabric.IText) {
                    const dataURL = textObj.toDataURL({
                        format: 'png',
                        multiplier: 6
                    })
                    text.dataURL = dataURL
    
                    // const div = document.createElement('div')
                    // div.style.display = 'inline-block'
                    // div.style.backgroundColor = 'black'
                    // div.style.width = `270px`
                    // div.style.height = `480px`
                    // div.style.position = 'relative'
                    // div.style.border = '1px solid gray'
    
                    // const image = document.createElement('img')
                    // image.src = dataURL
                    // image.style.position = 'absolute'
                    // image.style.left = `${left / 4}px`
                    // image.style.top = `${top / 4}px`
                    // image.style.width = `${width / 4}px`
                    // image.style.height = `${height / 4}px`
                    // div.appendChild(image)
    
                    // document.body.appendChild(div)

                    fabricCanvasRef.current.remove(textObj)
                }
            })
            fabricCanvasRef.current.renderAll()

            return {
                texts: results,
                layout: layoutType,
                volume: materialRef.current.volume,
                playbackSpeed: materialRef.current.playbackSpeed
            }
        }

        async function handleSave() {
            if (isSavingRef.current) return
            isSavingRef.current = true

            try {
                sortSentences()
                const material = JSON.stringify(ShortsHelper.CombineMaterialAndFabricTexts({
                    material: materialRef.current,
                    topicFabricText: fabricCanvasRef.current.getObjects().find(obj => obj.className === 'topic-text'),
                    sentenceFabricTexts: fabricCanvasRef.current.getObjects().filter(obj => obj.className === 'sentence-text'),
                    currentScale: currentCanvasScale
                }))

                await ShortsApis.PatchShorts(token, {
                    shortsId,
                    layout: layoutType,
                    material
                })
                setLastUpdated(null)
            }
            catch (e) {
                console.error(e)
            }
            
            isSavingRef.current = false
        }

        async function handleRendering() {
            if (!isCanRendering) return { result: false }

            if (isUploading || isRenderingRef.current) return { result: true }
            setIsUploading(true)
            isRenderingRef.current = true

            try {
                await handleSave()
                const renderingData = await extractReneringDatas()
    
                const { result } = await ShortsApis.PostRenderingShorts(token, {
                    shortsId,
                    renderingData
                })
                setIsUploading(false)
                isRenderingRef.current = false
    
                return {
                    result
                };
            }
            catch (e) {
                console.error(e)
            }
            
            setIsUploading(false)
            isRenderingRef.current = false

            return {
                result: false
            }
        }

        function clearCanvas() {
            try {
                if (fabricCanvasRef.current) {
                    fabricCanvasRef.current.dispose()
                    fabricCanvasRef.current = null
                }
            }
            catch (e) {}
        }

        const value = {
            isInitialized,
            isUploading,
            
            lastUpdated,
            index,
            isPreviewMode,
            isEditor,

            width,
            height,

            videoUrl,
            audioUrl,
            previewImageUrl,
            metadataJson,
            initData,

            LEFT_TAB_ITEMS,
            SCALE_WIDTH,
            ROW_HEIGHT,
            AUDIO_ROW_HEIGHT,

            leftTab,
            timelineData,
            isCanRendering,

            layoutType,
            fonts,
            timelineScale,
            currentScaleWidth,

            isPlaying,
            isPaused,
            videoTime,
            videoVolume,
            videoPlaybackSpeed,
            currentTimelineActions,
            currentSentence,
            currentActiveObject,
            videoWrapperRef,
            videoRef,
            videoRect,
            timelineRef,

            setLeftTab,
            handleUpdateLayout,
            setFonts,
            setTimelineScale,
            setCurrentTimelineActions,
            updateCurrentSentence,
            extractReneringDatas,

            setCanvasId,
            clearCanvas,

            getVideoStartTime,
            getVideoEndTime,

            handlePlayVideo,
            handlePauseVideo,
            handleStopVideo,
            handleMoveToStart,
            handleMoveToEnd,

            updateVideoVolume,
            updatePlaybackSpeed,
            updateVideoCurrentTime,
            updateSentence,
            updateSentences,

            updateTextOptions,

            handleSave,
            handleRendering,
        };

        return (
            <YoutubeUrlToShortsContext.Provider value={value}>
                <WrappedComponent {...props} ref={ref} />
            </YoutubeUrlToShortsContext.Provider>
        );
    });
};

export const withYoutubeUrlToShortsContext = (WrappedComponent) => {
    return forwardRef(function WithYoutubeUrlToShortsContextComponent(props, ref) {
        return (
            <YoutubeUrlToShortsContext.Consumer>
                {(context) => <WrappedComponent {...props} context={context} ref={ref} />}
            </YoutubeUrlToShortsContext.Consumer>
        );
    });
};